import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Home, Plus, X, ChevronLeft, ChevronRight, AlertCircle, TrendingUp, Users, Activity, Settings, FileText, ChevronDown, Eye, Edit, Mail, Trash2, Check, Calendar, Filter, Download, FileSpreadsheet, FileType } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import DashboardButton from '../components/DashboardButton';
import DashboardDropdown from '../components/DashboardDropdown';
import { format, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || API;

// Encon brand colors
const enconOrange = '#ee7203';

// Format date from YYYY-MM-DD to DD.MM.YY
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.substring(0, 10).split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0].substring(2)}`;
    return dateStr;
  } catch { return dateStr; }
};
const enconBlack = '#1d1d1b';
const enconGrey = '#9d9d9c';
const enconLightGrey = '#ededed';

// Error type color mapping - FIXED COLORS
const ERROR_TYPE_COLORS = {
  'Picking': '#ef4444',      // red
  'Checking': '#3b82f6',     // blue
  'Input': '#9333ea',        // purple
  'Delivery': '#eab308',     // yellow
  'Loading': '#22c55e',      // green
  'Invoice Query': '#0ea5e9' // sky blue
};

// Chart colors for other uses
const CHART_COLORS = ['#ee7203', '#8b4513', '#d2691e', '#ff8c00', '#ffa500', '#ff6347'];

const ErrorLog = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedCalendarYear, setSelectedCalendarYear] = useState(new Date().getFullYear());
  const [selectedMonthNumber, setSelectedMonthNumber] = useState(new Date().getMonth() + 1);
  const [entries, setEntries] = useState([]);
  // Click-to-filter state — clicking a person on the bar graph or an error
  // type in the side list filters the Entries list below by that value.
  // Click again (or the Clear pill) to reset.
  const [entryFilterPerson, setEntryFilterPerson] = useState(null);
  const [entryFilterType, setEntryFilterType] = useState(null);
  const [stats, setStats] = useState(null);
  const [yearStats, setYearStats] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [relatedEntries, setRelatedEntries] = useState([]);
  const [highlightedEntryId, setHighlightedEntryId] = useState(null);
  
  // Duplicate Order Detection State
  const [duplicateWarningOpen, setDuplicateWarningOpen] = useState(false);
  const [duplicateEntry, setDuplicateEntry] = useState(null);
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);
  
  // Service Issues State
  const [serviceIssues, setServiceIssues] = useState([]);
  const [serviceIssueModalOpen, setServiceIssueModalOpen] = useState(false);
  const [viewingServiceIssue, setViewingServiceIssue] = useState(null);
  const [editingServiceIssue, setEditingServiceIssue] = useState(null);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [trendModalOpen, setTrendModalOpen] = useState(false);
  
  // Reports State
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [viewReportsModalOpen, setViewReportsModalOpen] = useState(false);
  const [createReportModalOpen, setCreateReportModalOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportFilters, setReportFilters] = useState({
    dateRange: 'month', // month, quarter, year, custom
    startDate: '',
    endDate: '',
    customerNames: [],
    peopleInvolved: [],
    errorTypes: [],
    includeServiceIssues: true,
    includeErrors: true
  });
  const [reportName, setReportName] = useState('');
  const [editingReport, setEditingReport] = useState(null);
  
  // Autocomplete State
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [peopleSuggestions, setPeopleSuggestions] = useState([]);
  const [customerInput, setCustomerInput] = useState('');
  const [peopleInput, setPeopleInput] = useState('');
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [showPeopleSuggestions, setShowPeopleSuggestions] = useState(false);
  
  // Grouping State
  const [groupingModalOpen, setGroupingModalOpen] = useState(false);
  const [similarNames, setSimilarNames] = useState([]);
  const [selectedStandardName, setSelectedStandardName] = useState('');
  const [groupingType, setGroupingType] = useState(''); // 'customer' or 'people'
  
  // Similar name suggestions for form input (per name field)
  const [nameSimilarSuggestions, setNameSimilarSuggestions] = useState({}); // { index: [similar_names] }
  const [nameCheckTimeouts, setNameCheckTimeouts] = useState({}); // { index: timeoutId }
  
  // Report Viewer State
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [currentReportData, setCurrentReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [reportNotes, setReportNotes] = useState('');
  const [confirmNameModalOpen, setConfirmNameModalOpen] = useState(false);
  const [generatedReportName, setGeneratedReportName] = useState('');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  
  // Form fields
  const [orderNumber, setOrderNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [salesValue, setSalesValue] = useState('');
  const [contribution, setContribution] = useState('');
  const [names, setNames] = useState(['']);
  const [errorTypes, setErrorTypes] = useState([]);
  const [customErrorType, setCustomErrorType] = useState('');
  const [showAddErrorType, setShowAddErrorType] = useState(false);
  const [whatHappened, setWhatHappened] = useState('');
  const [resolution, setResolution] = useState('');
  const [cost, setCost] = useState(''); // Cost field
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Available error types
  const [availableErrorTypes, setAvailableErrorTypes] = useState([
    'Input', 'Picking', 'Checking', 'Loading', 'Delivery', 'Invoice Query'
  ]);
  
  const monthNames = [
    'August', 'September', 'October', 'November', 'December', 'January',
    'February', 'March', 'April', 'May', 'June', 'July'
  ];
  // Map month-tab display index (Aug=0..Jul=11) to calendar month (1=Jan..12=Dec)
  const TAB_TO_CAL_MONTH = [8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7];
  // Map calendar month (1-12) -> tab index (0=Aug..11=Jul)
  const calMonthToTabIdx = (m) => (m - 8 + 12) % 12;

  useEffect(() => {
    loadFinancialYears();
  }, []);

  // Keep selectedYear (FY) auto-derived from the current calendar year + month
  // so entry creation/reports/suggestions still get a sensible FY id.
  useEffect(() => {
    if (!financialYears.length) return;
    const probe = `${selectedCalendarYear}-${String(selectedMonthNumber).padStart(2, '0')}-15`;
    const fy = financialYears.find(y => probe >= y.year_start && probe <= y.year_end);
    setSelectedYear(fy || financialYears[0]);
  }, [financialYears, selectedCalendarYear, selectedMonthNumber]);

  useEffect(() => {
    loadEntries();
    loadServiceIssues();
    loadStats();
    loadYearStats();
  }, [selectedCalendarYear, selectedMonthNumber]);

  // Handle URL params for deep linking from global search
  useEffect(() => {
    const yearParam = searchParams.get('year');
    const monthParam = searchParams.get('month');
    const highlightParam = searchParams.get('highlight');
    
    if (yearParam && financialYears.length > 0) {
      // yearParam may be a FY id (legacy) or a calendar year (new)
      const matchFy = financialYears.find(y => y.id === yearParam);
      if (matchFy) {
        // Pick the start year of the FY as the calendar year
        const startYear = parseInt((matchFy.year_start || '').slice(0, 4), 10);
        if (!isNaN(startYear)) setSelectedCalendarYear(startYear);
      } else if (/^\d{4}$/.test(yearParam)) {
        setSelectedCalendarYear(parseInt(yearParam, 10));
      }
    }
    
    if (monthParam) {
      setSelectedMonthNumber(parseInt(monthParam, 10));
    }
    
    if (highlightParam) {
      setHighlightedEntryId(highlightParam);
      // Scroll to entry and clear highlight after animation
      setTimeout(() => {
        const element = document.getElementById(`entry-${highlightParam}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
      
      // Clear highlight after 5 seconds
      setTimeout(() => {
        setHighlightedEntryId(null);
        // Clear URL params
        navigate('/error-log', { replace: true });
      }, 5000);
    }
  }, [searchParams, financialYears, navigate]);

  const loadFinancialYears = async () => {
    try {
      const response = await api.get(`${API}/financial-years`);
      const years = response.data.financial_years || response.data;
      setFinancialYears(years);
      // selectedCalendarYear / selectedMonthNumber default to today (set on init).
      // The selectedYear (FY) is derived via useEffect once financialYears is loaded.
      setLoading(false);
    } catch (error) {
      console.error('Error loading financial years:', error);
      toast.error('Failed to load financial years');
      setLoading(false);
    }
  };

  const loadEntries = async () => {
    try {
      const response = await api.get(`${API}/error-log/entries`, {
        params: {
          calendar_year: selectedCalendarYear,
          month_number: selectedMonthNumber
        }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error loading entries:', error);
      toast.error('Failed to load error log entries');
    }
  };

  const loadServiceIssues = async () => {
    try {
      const response = await api.get(`${API}/service-issues`, {
        params: {
          calendar_year: selectedCalendarYear,
          month_number: selectedMonthNumber
        }
      });
      setServiceIssues(response.data.service_issues || []);
    } catch (error) {
      console.error('Error loading service issues:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get(`${API}/error-log/stats`, {
        params: {
          calendar_year: selectedCalendarYear,
          month_number: selectedMonthNumber
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadYearStats = async () => {
    try {
      const response = await api.get(`${API}/error-log/stats`, {
        params: {
          calendar_year: selectedCalendarYear
        }
      });
      setYearStats(response.data);
    } catch (error) {
      console.error('Error loading year stats:', error);
    }
  };

  const lookupOrderDetails = async (orderNum) => {
    if (!orderNum) return;
    
    try {
      // Check for duplicate order number in error log (only when adding new entry, not editing)
      if (!editingEntry && !duplicateAcknowledged) {
        const duplicateCheck = await api.get(`${API}/error-log/check-duplicate`, {
          params: { order_number: orderNum }
        });
        
        if (duplicateCheck.data.exists && duplicateCheck.data.entry) {
          setDuplicateEntry(duplicateCheck.data.entry);
          setDuplicateWarningOpen(true);
          return; // Stop processing until user acknowledges
        }
      }
      
      // Use new comprehensive endpoint
      const response = await api.get(`${API}/upload/order-details/${orderNum}`);
      
      if (response.data) {
        // Set customer name if found
        if (response.data.customer_name) {
          setCustomerName(response.data.customer_name);
        }
        
        // Set sales value if found
        if (response.data.sales_value !== null && response.data.sales_value !== undefined) {
          setSalesValue(response.data.sales_value.toString());
        }
        
        // Set contribution if found
        if (response.data.contribution !== null && response.data.contribution !== undefined) {
          setContribution(response.data.contribution.toString());
        }
        
        // If no customer name from CSV, try transport logs as fallback
        if (!response.data.customer_name) {
          const searchResponse = await api.post(`${API}/search`, {
            search_type: 'order_number',
            query: orderNum
          });
          
          if (searchResponse.data.results && searchResponse.data.results.length > 0) {
            const result = searchResponse.data.results[0];
            if (result.entry && result.entry.customer_name) {
              setCustomerName(result.entry.customer_name);
            }
          }
        }
      }
    } catch (error) {
      console.log('Order lookup completed with partial data');
    }
  };
  
  // Handle duplicate warning response
  const handleDuplicateContinue = () => {
    setDuplicateAcknowledged(true);
    setDuplicateWarningOpen(false);
    // Continue with order lookup now that duplicate is acknowledged
    lookupOrderDetailsAfterAcknowledge(orderNumber);
  };
  
  const handleDuplicateDiscard = () => {
    setDuplicateWarningOpen(false);
    setDuplicateEntry(null);
    setModalOpen(false);
    // Reset form
    setOrderNumber('');
    setCustomerName('');
    setSalesValue('');
    setContribution('');
    setNames(['']);
    setNameSimilarSuggestions({});
    setErrorTypes([]);
    setWhatHappened('');
    setResolution('');
  };
  
  // Separate function for lookup after acknowledging duplicate
  const lookupOrderDetailsAfterAcknowledge = async (orderNum) => {
    if (!orderNum) return;
    
    try {
      const response = await api.get(`${API}/upload/order-details/${orderNum}`);
      
      if (response.data) {
        if (response.data.customer_name) {
          setCustomerName(response.data.customer_name);
        }
        if (response.data.sales_value !== null && response.data.sales_value !== undefined) {
          setSalesValue(response.data.sales_value.toString());
        }
        if (response.data.contribution !== null && response.data.contribution !== undefined) {
          setContribution(response.data.contribution.toString());
        }
        
        if (!response.data.customer_name) {
          const searchResponse = await api.post(`${API}/search`, {
            search_type: 'order_number',
            query: orderNum
          });
          
          if (searchResponse.data.results && searchResponse.data.results.length > 0) {
            const result = searchResponse.data.results[0];
            if (result.entry && result.entry.customer_name) {
              setCustomerName(result.entry.customer_name);
            }
          }
        }
      }
    } catch (error) {
      console.log('Order lookup completed with partial data');
    }
  };

  const openAddModal = () => {
    setEditingEntry(null);
    setOrderNumber('');
    setCustomerName('');
    setCost('');
    setSalesValue('');
    setContribution('');
    setNames(['']);
    setNameSimilarSuggestions({});
    setErrorTypes([]);
    setCustomErrorType('');
    setShowAddErrorType(false);
    setWhatHappened('');
    setResolution('');
    setEntryDate(new Date().toISOString().split('T')[0]);
    setDuplicateAcknowledged(false);
    setDuplicateEntry(null);
    setModalOpen(true);
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setOrderNumber(entry.order_number || '');
    setCustomerName(entry.customer_name || '');
    setCost(entry.cost?.toString() || '');
    setSalesValue(entry.sales_value?.toString() || '');
    setContribution(entry.contribution?.toString() || '');
    setNames(entry.names && entry.names.length > 0 ? entry.names : ['']);
    setNameSimilarSuggestions({});
    setErrorTypes(entry.error_types || []);
    setCustomErrorType('');
    setShowAddErrorType(false);
    setWhatHappened(entry.what_happened || '');
    setResolution(entry.resolution || '');
    setEntryDate(entry.date || new Date().toISOString().split('T')[0]);
    setModalOpen(true);
  };

  const openViewModal = async (entry) => {
    setViewingEntry(entry);
    setViewModalOpen(true);
    
    // Load related entries by same person within current calendar year
    try {
      const response = await api.get(`${API}/error-log/entries`, {
        params: {
          calendar_year: selectedCalendarYear
        }
      });
      
      const allEntries = response.data.entries || [];
      // Filter entries that have at least one matching name and exclude current entry
      const related = allEntries.filter(e => 
        e.id !== entry.id && 
        e.names && 
        entry.names && 
        e.names.some(name => entry.names.includes(name))
      ).slice(0, 3); // Show max 3 related
      
      setRelatedEntries(related);
    } catch (error) {
      console.error('Error loading related entries:', error);
      setRelatedEntries([]);
    }
  };


  const handleSaveEntry = async () => {
    if (!orderNumber) {
      toast.error('Please enter an order number');
      return;
    }
    
    if (errorTypes.length === 0) {
      toast.error('Please select at least one error type');
      return;
    }
    
    const filteredNames = names.filter(n => n.trim() !== '');
    if (filteredNames.length === 0) {
      toast.error('Please enter at least one name');
      return;
    }
    
    try {
      // Derive correct financial_year_id and FY-month_number from entryDate
      const fyForDate = financialYears.find(
        y => entryDate >= y.year_start && entryDate <= y.year_end
      ) || selectedYear;
      
      let fyMonthNumber = selectedMonthNumber;
      if (fyForDate) {
        const startDate = new Date(fyForDate.year_start);
        const dObj = new Date(entryDate);
        const diff = (dObj.getFullYear() - startDate.getFullYear()) * 12 +
                     (dObj.getMonth() - startDate.getMonth()) + 1;
        fyMonthNumber = Math.min(Math.max(diff, 1), 12);
      }
      
      const entryData = {
        financial_year_id: fyForDate ? fyForDate.id : (selectedYear?.id || ''),
        date: entryDate,
        month_number: fyMonthNumber,
        order_number: orderNumber,
        customer_name: customerName,
        cost: parseFloat(cost) || 0,
        sales_value: parseFloat(salesValue) || 0,
        contribution: parseFloat(contribution) || 0,
        names: filteredNames,
        error_types: errorTypes,
        what_happened: whatHappened,
        resolution: resolution
      };
      
      if (editingEntry && editingEntry.id) {
        await api.put(`${API}/error-log/entry/${editingEntry.id}`, entryData);
        toast.success('Entry updated');
      } else {
        await api.post(`${API}/error-log/entry`, entryData);
        toast.success('Entry created');
      }
      
      setModalOpen(false);
      loadEntries();
      loadStats();
    } catch (error) {
      console.error('Error saving entry:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save entry';
      toast.error(errorMessage);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      await api.delete(`${API}/error-log/entry/${entryId}`);
      toast.success('Entry deleted');
      setViewModalOpen(false);
      loadEntries();
      loadStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete entry';
      toast.error(errorMessage);
    }
  };

  const addName = () => {
    setNames([...names, '']);
  };

  const removeName = (index) => {
    if (names.length > 1) {
      setNames(names.filter((_, i) => i !== index));
      // Clear similar suggestions for this index
      setNameSimilarSuggestions(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  const updateName = (index, value) => {
    const newNames = [...names];
    newNames[index] = value;
    setNames(newNames);
    
    // Check for similar names after debounce
    if (nameCheckTimeouts[index]) {
      clearTimeout(nameCheckTimeouts[index]);
    }
    
    if (value.trim().length >= 2) {
      const timeoutId = setTimeout(async () => {
        try {
          const response = await api.get(`${BACKEND_URL}/api/error-log/check-similar-names`, {
            params: { name: value.trim(), type: 'people' }
          });
          if (response.data.similar_names && response.data.similar_names.length > 0) {
            setNameSimilarSuggestions(prev => ({
              ...prev,
              [index]: response.data.similar_names
            }));
          } else {
            setNameSimilarSuggestions(prev => {
              const updated = { ...prev };
              delete updated[index];
              return updated;
            });
          }
        } catch (error) {
          console.error('Error checking similar names:', error);
        }
      }, 400); // 400ms debounce
      
      setNameCheckTimeouts(prev => ({
        ...prev,
        [index]: timeoutId
      }));
    } else {
      // Clear suggestions if input is too short
      setNameSimilarSuggestions(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  const selectSimilarName = (index, suggestedName) => {
    const newNames = [...names];
    newNames[index] = suggestedName;
    setNames(newNames);
    // Clear the suggestion for this field
    setNameSimilarSuggestions(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const dismissSimilarSuggestion = (index) => {
    setNameSimilarSuggestions(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const toggleErrorType = (type) => {
    if (errorTypes.includes(type)) {
      setErrorTypes(errorTypes.filter(t => t !== type));
    } else {
      setErrorTypes([...errorTypes, type]);
    }
  };

  const addCustomErrorType = () => {
    if (customErrorType && !availableErrorTypes.includes(customErrorType)) {
      setAvailableErrorTypes([...availableErrorTypes, customErrorType]);
      setErrorTypes([...errorTypes, customErrorType]);
      setCustomErrorType('');
    }
  };

  const navigateYear = (direction) => {
    setSelectedCalendarYear(prev => prev + direction);
  };

  // Autocomplete Functions
  const loadCustomerSuggestions = async (query = '') => {
    try {
      console.log('🔍 Loading customer suggestions for query:', query);
      // Don't filter by year - show all customer names across all years for better UX
      const response = await api.get(`${BACKEND_URL}/api/error-log/customer-names`, {
        params: { query }
      });
      console.log('✅ Customer suggestions received:', response.data.names);
      setCustomerSuggestions(response.data.names || []);
    } catch (error) {
      console.error('❌ Error loading customer suggestions:', error);
    }
  };

  const loadPeopleSuggestions = async (query = '') => {
    try {
      console.log('🔍 Loading people suggestions for query:', query);
      // Don't filter by year - show all people names across all years for better UX
      const response = await api.get(`${BACKEND_URL}/api/error-log/people-names`, {
        params: { query }
      });
      console.log('✅ People suggestions received:', response.data.names);
      setPeopleSuggestions(response.data.names || []);
    } catch (error) {
      console.error('❌ Error loading people suggestions:', error);
    }
  };

  const checkSimilarNames = async (name, type) => {
    try {
      const response = await api.get(`${BACKEND_URL}/api/error-log/check-similar-names`, {
        params: { name, type, year_id: selectedYear?.id }
      });
      
      if (response.data.similar_names && response.data.similar_names.length > 0) {
        setSimilarNames([name, ...response.data.similar_names]);
        setSelectedStandardName(name);
        setGroupingType(type);
        setGroupingModalOpen(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking similar names:', error);
      return false;
    }
  };

  const groupSimilarNames = async () => {
    try {
      await api.post(`${BACKEND_URL}/api/error-log/group-names`, {
        names: similarNames,
        standard_name: selectedStandardName,
        type: groupingType,
        year_id: selectedYear?.id
      });
      
      toast.success(`Successfully grouped names under "${selectedStandardName}"`);
      setGroupingModalOpen(false);
      
      // Add the standardized name to filters
      if (groupingType === 'customer') {
        setReportFilters({
          ...reportFilters,
          customerNames: [...reportFilters.customerNames, selectedStandardName]
        });
        setCustomerInput('');
      } else {
        setReportFilters({
          ...reportFilters,
          peopleInvolved: [...reportFilters.peopleInvolved, selectedStandardName]
        });
        setPeopleInput('');
      }
      
      // Reload data to reflect changes
      if (selectedYear) {
        loadEntries();
        loadServiceIssues();
      }
    } catch (error) {
      console.error('Error grouping names:', error);
      toast.error('Failed to group names');
    }
  };

  const handleCustomerInputChange = async (value) => {
    setCustomerInput(value);
    if (value.length >= 2) {
      await loadCustomerSuggestions(value);
      setShowCustomerSuggestions(true);
    } else {
      setShowCustomerSuggestions(false);
    }
  };

  const handlePeopleInputChange = async (value) => {
    setPeopleInput(value);
    if (value.length >= 2) {
      await loadPeopleSuggestions(value);
      setShowPeopleSuggestions(true);
    } else {
      setShowPeopleSuggestions(false);
    }
  };

  const addCustomerName = async (name) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    
    // Check if already added
    if (reportFilters.customerNames.includes(trimmedName)) {
      toast.info('Customer already added');
      return;
    }
    
    // Check for similar names
    const hasSimilar = await checkSimilarNames(trimmedName, 'customer');
    
    if (!hasSimilar) {
      setReportFilters({
        ...reportFilters,
        customerNames: [...reportFilters.customerNames, trimmedName]
      });
      setCustomerInput('');
      setShowCustomerSuggestions(false);
    }
  };

  const addPersonName = async (name) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    
    // Check if already added
    if (reportFilters.peopleInvolved.includes(trimmedName)) {
      toast.info('Person already added');
      return;
    }
    
    // Check for similar names
    const hasSimilar = await checkSimilarNames(trimmedName, 'people');
    
    if (!hasSimilar) {
      setReportFilters({
        ...reportFilters,
        peopleInvolved: [...reportFilters.peopleInvolved, trimmedName]
      });
      setPeopleInput('');
      setShowPeopleSuggestions(false);
    }
  };

  const removeCustomerName = (name) => {
    setReportFilters({
      ...reportFilters,
      customerNames: reportFilters.customerNames.filter(n => n !== name)
    });
  };

  const removePersonName = (name) => {
    setReportFilters({
      ...reportFilters,
      peopleInvolved: reportFilters.peopleInvolved.filter(n => n !== name)
    });
  };

  // Reports Functions
  const loadReports = async () => {
    try {
      const response = await api.get(`${BACKEND_URL}/api/error-reports`);
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    }
  };

  const generateReportName = () => {
    const dateRangeText = reportFilters.dateRange === 'month' ? 'Monthly' :
                          reportFilters.dateRange === 'quarter' ? 'Quarterly' :
                          reportFilters.dateRange === 'year' ? 'Yearly' : 'Custom';
    
    const types = [];
    if (reportFilters.includeErrors) types.push('Errors');
    if (reportFilters.includeServiceIssues) types.push('Service Issues');
    
    const date = format(new Date(), 'MMM yyyy');
    
    // Add specific filters to name
    const filters = [];
    
    if (reportFilters.errorTypes.length > 0) {
      filters.push(reportFilters.errorTypes.join(', '));
    }
    
    if (reportFilters.customerNames.length > 0) {
      if (reportFilters.customerNames.length === 1) {
        filters.push(reportFilters.customerNames[0]);
      } else {
        filters.push(`${reportFilters.customerNames.length} Customers`);
      }
    }
    
    if (reportFilters.peopleInvolved.length > 0) {
      if (reportFilters.peopleInvolved.length === 1) {
        filters.push(reportFilters.peopleInvolved[0]);
      } else {
        filters.push(`${reportFilters.peopleInvolved.length} People`);
      }
    }
    
    let name = `${dateRangeText} ${types.join(' & ')} Report`;
    if (filters.length > 0) {
      name += ` - ${filters.join(', ')}`;
    }
    name += ` (${date})`;
    
    return name;
  };

  const openCreateReportModal = () => {
    setReportsDropdownOpen(false);
    // Default the modal's date range to the calendar month the user is
    // currently browsing on the page (selectedCalendarYear +
    // selectedMonthNumber) rather than today's calendar month — otherwise
    // a user looking at April 2026 entries clicks Create Report and lands
    // on a May report with zero data.
    const monthIdx = (selectedMonthNumber || (new Date().getMonth() + 1)) - 1; // 0-11
    const probe = new Date(selectedCalendarYear || new Date().getFullYear(), monthIdx, 15);
    setReportFilters({
      dateRange: 'month',
      startDate: format(startOfMonth(probe), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(probe), 'yyyy-MM-dd'),
      customerNames: [],
      peopleInvolved: [],
      errorTypes: [],
      includeServiceIssues: true,
      includeErrors: true
    });
    setReportName('');
    setEditingReport(null);
    setCreateReportModalOpen(true);
  };

  const openViewReportsModal = async () => {
    setReportsDropdownOpen(false);
    await loadReports();
    setViewReportsModalOpen(true);
  };

  const handleDateRangeChange = (range) => {
    // For 'month', use the calendar month the user is currently browsing on
    // the page (selectedMonthNumber + selectedCalendarYear) rather than
    // today, so the create-report flow honours the active month tab.
    const monthIdx = (selectedMonthNumber || (new Date().getMonth() + 1)) - 1;
    const probeForMonth = new Date(selectedCalendarYear || new Date().getFullYear(), monthIdx, 15);
    const now = new Date();
    let start, end;

    switch(range) {
      case 'month':
        start = startOfMonth(probeForMonth);
        end = endOfMonth(probeForMonth);
        break;
      case 'quarter':
        start = startOfQuarter(now);
        end = endOfQuarter(now);
        break;
      case 'year':
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      default:
        start = now;
        end = now;
    }
    
    setReportFilters({
      ...reportFilters,
      dateRange: range,
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    });
  };

  const fetchReportData = async (report) => {
    setReportLoading(true);
    try {
      console.log('📊 Fetching report data with filters:', report.filters);
      const response = await api.post(`${BACKEND_URL}/api/error-reports/generate`, {
        filters: report.filters,
        year_id: report.year_id
      });
      console.log('✅ Report data received:', response.data);
      console.log('  - Errors:', response.data.errors?.length);
      console.log('  - Service Issues:', response.data.service_issues?.length);
      console.log('  - Service Issues by Customer keys:', Object.keys(response.data.service_issues_by_customer || {}));
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching report data:', error);
      toast.error('Failed to generate report data');
      return null;
    } finally {
      setReportLoading(false);
    }
  };

  const createReport = async () => {
    // Generate name based on filters
    const autoName = generateReportName();
    setGeneratedReportName(autoName);
    setReportName(autoName);
    
    // Close create modal and show confirmation modal
    setCreateReportModalOpen(false);
    setConfirmNameModalOpen(true);
  };

  const confirmAndSaveReport = async () => {
    try {
      const reportData = {
        name: reportName,
        filters: reportFilters,
        created_at: new Date().toISOString(),
        year_id: selectedYear?.id
      };

      const response = await api.post(`${BACKEND_URL}/api/error-reports`, reportData);
      toast.success('Report created successfully!');
      setConfirmNameModalOpen(false);
      setCreateReportModalOpen(false);
      await loadReports();
      
      // Auto-open the report
      const newReport = response.data.report;
      await viewReport(newReport);
    } catch (error) {
      console.error('Error creating report:', error);
      toast.error('Failed to create report');
    }
  };

  const viewReport = async (report) => {
    setSelectedReport(report);
    setViewReportsModalOpen(false);
    
    // Load existing notes
    setReportNotes(report.notes || '');
    setNotesOpen(false);
    
    // Fetch report data
    const reportData = await fetchReportData(report);
    if (reportData) {
      setCurrentReportData(reportData);
      setReportViewerOpen(true);
    }
  };

  const saveReportNotes = async () => {
    try {
      await api.put(`${BACKEND_URL}/api/error-reports/${selectedReport.id}`, {
        name: selectedReport.name,
        filters: selectedReport.filters,
        notes: reportNotes
      });
      toast.success('Notes saved successfully!');
      setNotesOpen(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const exportToExcel = () => {
    if (!currentReportData || !selectedReport) {
      toast.error('No report data to export');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      
      // Summary Sheet
      const summaryData = [
        ['Report Name', selectedReport.name],
        ['Generated Date', format(new Date(), 'dd/MM/yyyy HH:mm')],
        ['Report Period', `${format(new Date(currentReportData.summary.date_range.start), 'dd/MM/yyyy')} - ${format(new Date(currentReportData.summary.date_range.end), 'dd/MM/yyyy')}`],
        [],
        ['Summary Statistics'],
        ['Total Errors', currentReportData.summary.total_errors],
        ['Total Service Issues', currentReportData.summary.total_service_issues],
        ['Error Types', currentReportData.summary.error_types_count],
        ['Customers', currentReportData.summary.customers_count]
      ];
      
      if (reportNotes) {
        summaryData.push([], ['Notes'], [reportNotes]);
      }
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      // Auto-size columns for summary
      summarySheet['!cols'] = [
        { wch: 20 }, // Column A width
        { wch: 50 }  // Column B width
      ];
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

      // Errors by Type Sheet
      if (currentReportData.errors_by_type && Object.keys(currentReportData.errors_by_type).length > 0) {
        const errorsData = [['Error Type', 'Order Number', 'Date', 'Customer', 'Description', 'People Involved']];
        
        Object.entries(currentReportData.errors_by_type).forEach(([errorType, errors]) => {
          errors.forEach(error => {
            errorsData.push([
              errorType,
              error.order_number || 'N/A',
              format(new Date(error.date), 'dd/MM/yyyy'),
              error.customer_name || 'N/A',
              error.description || error.what_happened || 'No description',
              error.names ? error.names.join(', ') : ''
            ]);
          });
        });
        
        const errorsSheet = XLSX.utils.aoa_to_sheet(errorsData);
        // Auto-size columns for errors
        errorsSheet['!cols'] = [
          { wch: 15 }, // Error Type
          { wch: 12 }, // Order Number
          { wch: 12 }, // Date
          { wch: 20 }, // Customer
          { wch: 50 }, // Description
          { wch: 30 }  // People Involved
        ];
        XLSX.utils.book_append_sheet(wb, errorsSheet, 'Errors by Type');
      }

      // Service Issues by Customer Sheet
      if (currentReportData.service_issues_by_customer && Object.keys(currentReportData.service_issues_by_customer).length > 0) {
        const issuesData = [['Customer', 'Date', 'Category', 'Order Number', 'Postcode', 'Notes']];
        
        Object.entries(currentReportData.service_issues_by_customer).forEach(([customer, issues]) => {
          issues.forEach(issue => {
            issuesData.push([
              customer,
              format(new Date(issue.date), 'dd/MM/yyyy'),
              issue.category || issue.issue_type || 'Service Issue',
              issue.order_number || 'N/A',
              issue.postcode || 'N/A',
              issue.notes || ''
            ]);
          });
        });
        
        const issuesSheet = XLSX.utils.aoa_to_sheet(issuesData);
        // Auto-size columns for service issues
        issuesSheet['!cols'] = [
          { wch: 20 }, // Customer
          { wch: 12 }, // Date
          { wch: 20 }, // Category
          { wch: 15 }, // Order Number
          { wch: 12 }, // Postcode
          { wch: 40 }  // Notes
        ];
        XLSX.utils.book_append_sheet(wb, issuesSheet, 'Service Issues');
      }

      // Generate filename and download
      const filename = `${selectedReport.name.replace(/[^a-z0-9]/gi, '_')}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  const exportReportFromList = async (report, format) => {
    try {
      toast.info(`Preparing ${format.toUpperCase()} export...`);
      
      // Fetch report data
      const reportData = await fetchReportData(report);
      if (!reportData) {
        toast.error('Failed to fetch report data');
        return;
      }

      // Temporarily set the data
      const prevReport = selectedReport;
      const prevData = currentReportData;
      const prevNotes = reportNotes;
      
      setSelectedReport(report);
      setCurrentReportData(reportData);
      setReportNotes(report.notes || '');
      
      // Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Export based on format
      if (format === 'excel') {
        exportToExcel();
      } else if (format === 'pdf') {
        exportToPDF();
      }
      
      // Restore previous state
      setTimeout(() => {
        setSelectedReport(prevReport);
        setCurrentReportData(prevData);
        setReportNotes(prevNotes);
      }, 500);
      
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const exportToPDF = () => {
    if (!currentReportData || !selectedReport) {
      toast.error('No report data to export');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.setTextColor(238, 114, 3); // Encon Orange
      doc.text(selectedReport.name, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Report Info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text(`Period: ${format(new Date(currentReportData.summary.date_range.start), 'dd/MM/yyyy')} - ${format(new Date(currentReportData.summary.date_range.end), 'dd/MM/yyyy')}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Summary Statistics
      doc.setFontSize(14);
      doc.setTextColor(238, 114, 3);
      doc.text('Summary Statistics', 14, yPos);
      yPos += 8;

      const summaryData = [
        ['Total Errors', currentReportData.summary.total_errors.toString()],
        ['Total Service Issues', currentReportData.summary.total_service_issues.toString()],
        ['Error Types', currentReportData.summary.error_types_count.toString()],
        ['Customers', currentReportData.summary.customers_count.toString()]
      ];

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Count']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [238, 114, 3] },
        margin: { left: 14, right: 14 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Errors by Type
      if (currentReportData.errors_by_type && Object.keys(currentReportData.errors_by_type).length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(238, 114, 3);
        doc.text('Errors by Type', 14, yPos);
        yPos += 8;

        const errorsData = [];
        Object.entries(currentReportData.errors_by_type).forEach(([errorType, errors]) => {
          errors.forEach(error => {
            errorsData.push([
              errorType,
              error.order_number || 'N/A',
              format(new Date(error.date), 'dd/MM/yyyy'),
              error.customer_name || 'N/A',
              error.description || error.what_happened || 'No description',
              error.names ? error.names.join(', ') : ''
            ]);
          });
        });

        autoTable(doc, {
          startY: yPos,
          head: [['Error Type', 'Order #', 'Date', 'Customer', 'Description', 'People']],
          body: errorsData,
          theme: 'grid',
          headStyles: { fillColor: [238, 114, 3] },
          margin: { left: 14, right: 14 },
          styles: { fontSize: 7, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 22 },  // Error Type
            1: { cellWidth: 18 },  // Order #
            2: { cellWidth: 20 },  // Date
            3: { cellWidth: 25 },  // Customer
            4: { cellWidth: 60 },  // Description
            5: { cellWidth: 35 }   // People
          }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Service Issues by Customer
      if (currentReportData.service_issues_by_customer && Object.keys(currentReportData.service_issues_by_customer).length > 0) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(238, 114, 3);
        doc.text('Service Issues by Customer', 14, yPos);
        yPos += 8;

        const issuesData = [];
        Object.entries(currentReportData.service_issues_by_customer).forEach(([customer, issues]) => {
          issues.forEach(issue => {
            issuesData.push([
              customer,
              format(new Date(issue.date), 'dd/MM/yyyy'),
              issue.category || issue.issue_type || 'Service Issue',
              issue.order_number || 'N/A',
              issue.postcode || 'N/A'
            ]);
          });
        });

        autoTable(doc, {
          startY: yPos,
          head: [['Customer', 'Date', 'Category', 'Order', 'Postcode']],
          body: issuesData,
          theme: 'grid',
          headStyles: { fillColor: [238, 114, 3] },
          margin: { left: 14, right: 14 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 40 },  // Customer
            1: { cellWidth: 25 },  // Date
            2: { cellWidth: 40 },  // Category
            3: { cellWidth: 30 },  // Order
            4: { cellWidth: 25 }   // Postcode
          }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Notes
      if (reportNotes && reportNotes.trim()) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(238, 114, 3);
        doc.text('Notes', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const splitNotes = doc.splitTextToSize(reportNotes, pageWidth - 28);
        doc.text(splitNotes, 14, yPos);
      }

      // Save PDF
      const filename = `${selectedReport.name.replace(/[^a-z0-9]/gi, '_')}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      doc.save(filename);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF');
    }
  };

  const editReport = (report) => {
    setEditingReport(report);
    setReportName(report.name);
    setReportFilters(report.filters);
    setViewReportsModalOpen(false);
    setCreateReportModalOpen(true);
  };

  const updateReport = async () => {
    try {
      const reportData = {
        name: reportName,
        filters: reportFilters,
        updated_at: new Date().toISOString()
      };

      await api.put(`${BACKEND_URL}/api/error-reports/${editingReport.id}`, reportData);
      toast.success('Report updated successfully!');
      setCreateReportModalOpen(false);
      setEditingReport(null);
      await loadReports();
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const emailReport = async (report) => {
    try {
      await api.post(`${BACKEND_URL}/api/error-reports/${report.id}/email`);
      toast.success('Report sent via email!');
    } catch (error) {
      console.error('Error emailing report:', error);
      toast.error('Failed to send report');
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await api.delete(`${BACKEND_URL}/api/error-reports/${reportId}`);
      toast.success('Report deleted successfully!');
      await loadReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const errorTypeData = stats?.errors_by_type ? 
    Object.entries(stats.errors_by_type).map(([type, count]) => ({ name: type, value: count })) : [];
  
  const errorPersonData = stats?.errors_by_person ? 
    Object.entries(stats.errors_by_person).map(([name, count]) => ({ name, errors: count })) : [];
  
  const monthlyTrendData = stats?.monthly_trend || [];

  return (
    <>
    <div className="min-h-screen" style={{ backgroundColor: enconLightGrey }}>
      {/* App Header */}
      <AppHeader 
        user={user}
        onLogout={() => {}}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Error Log</span>
          </div>
        }
        subHeaderRight={
          <>
            <button
              onClick={openAddModal}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
              style={{ color: enconOrange }}
              title="Add Entry"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add Entry</span>
            </button>
            
            {/* Reports Dropdown Button */}
            <div className="relative">
              <button
                onClick={() => setReportsDropdownOpen(!reportsDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
                style={{ color: enconOrange }}
                title="Reports"
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Reports</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${reportsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Reports Dropdown Menu */}
              {reportsDropdownOpen && (
                <div 
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border-2 z-50"
                  style={{ borderColor: enconOrange }}
                >
                  <button
                    onClick={openCreateReportModal}
                    className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-2 transition-colors"
                    style={{ color: enconOrange }}
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium">Create Report</span>
                  </button>
                  <div className="border-t" style={{ borderColor: enconLightGrey }}></div>
                  <button
                    onClick={openViewReportsModal}
                    className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-2 transition-colors rounded-b-lg"
                    style={{ color: enconOrange }}
                  >
                    <Eye className="w-4 h-4" />
                    <span className="font-medium">View Reports</span>
                  </button>
                </div>
              )}
            </div>
          </>
        }
        mobileMenuButtons={[
          { icon: Plus, label: 'Add Entry', onClick: openAddModal, color: enconOrange },
          { icon: FileText, label: 'Reports', onClick: () => setReportsDropdownOpen(!reportsDropdownOpen), color: enconOrange }
        ]}
      />

      {/* Main Content */}
      <div className="px-4 lg:px-8 py-6 pt-[88px] space-y-6">
        
        {/* Calendar Year Navigation with Month Tabs */}
        <div className="bg-white rounded-lg shadow-md p-3">
          {/* Year above */}
          <h2 className="text-center text-base font-bold mb-2" style={{ color: enconBlack }} data-testid="error-log-year-label">
            {selectedCalendarYear}
          </h2>
          
          {/* Month Tabs with arrows on same line */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateYear(-1)}
              data-testid="error-log-prev-year-btn"
              className="p-1.5 rounded-lg hover:bg-gray-100 flex-shrink-0"
              style={{ color: enconOrange }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="flex flex-1 bg-gray-100 rounded-xl p-1">
              {monthNames.map((month, index) => {
                const calMonth = TAB_TO_CAL_MONTH[index];
                const isActive = selectedMonthNumber === calMonth;
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedMonthNumber(calMonth)}
                    data-testid={`error-log-month-tab-${month.toLowerCase()}`}
                    className={`flex-1 px-2 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                      isActive ? 'text-white shadow-md' : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    style={isActive ? { backgroundColor: enconOrange } : {}}
                  >
                    {month.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => navigateYear(1)}
              data-testid="error-log-next-year-btn"
              className="p-1.5 rounded-lg hover:bg-gray-100 flex-shrink-0"
              style={{ color: enconOrange }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Highlights Section - Reordered */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Errors - Orange */}
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-orange-900 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Total Errors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{stats?.total_errors || 0}</div>
              <p className="text-xs text-orange-700 mt-1">This month</p>
            </CardContent>
          </Card>
          
          {/* Average Errors/Month - Based on 7 months */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Avg Errors/Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {yearStats?.monthly_trend ? 
                  (() => {
                    const currentMonthIndex = calMonthToTabIdx(selectedMonthNumber);
                    const startIndex = Math.max(0, currentMonthIndex - 6);
                    const endIndex = currentMonthIndex + 1;
                    const last7Months = yearStats.monthly_trend.slice(startIndex, endIndex);
                    const avg = last7Months.reduce((sum, m) => sum + m.count, 0) / last7Months.length;
                    return avg > 0 ? Math.round(avg) : 0;
                  })() 
                  : 0
                }
              </div>
              <p className="text-xs text-blue-700 mt-1">Last 7 months</p>
            </CardContent>
          </Card>
          
          {/* Highest Error Type this month */}
          {(() => {
            const highestType = stats?.errors_by_type ? 
              Object.entries(stats.errors_by_type).sort((a, b) => b[1] - a[1])[0] : null;
            const typeName = highestType ? highestType[0] : 'None';
            const typeCount = highestType ? highestType[1] : 0;
            const typeColor = ERROR_TYPE_COLORS[typeName] || '#9333ea';
            
            return (
              <Card className="border-2" style={{ 
                background: `linear-gradient(to bottom right, ${typeColor}15, ${typeColor}30)`,
                borderColor: `${typeColor}50`
              }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2" style={{ color: typeColor }}>
                    <Activity className="w-4 h-4" />
                    {typeName}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ color: typeColor }}>
                    {typeCount}
                  </div>
                  <p className="text-xs mt-1" style={{ color: typeColor }}>Highest this month</p>
                </CardContent>
              </Card>
            );
          })()}
          
          {/* Next Month Target */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Next Month Target
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {yearStats?.monthly_trend ? 
                  (() => {
                    const currentMonthIndex = calMonthToTabIdx(selectedMonthNumber);
                    const startIndex = Math.max(0, currentMonthIndex - 6);
                    const endIndex = currentMonthIndex + 1;
                    const last7Months = yearStats.monthly_trend.slice(startIndex, endIndex);
                    const avg = last7Months.reduce((sum, m) => sum + m.count, 0) / last7Months.length;
                    const target = Math.max(0, Math.round(avg - 2));
                    return target;
                  })() 
                  : 0
                }
              </div>
              <p className="text-xs text-green-700 mt-1">Avg - 2</p>
            </CardContent>
          </Card>
        </div>


        {/* Monthly Trend Chart - Rolling 7-Month View - MOVED UNDER CARDS */}
        {yearStats && yearStats.monthly_trend && (() => {
          const currentMonthIndex = calMonthToTabIdx(selectedMonthNumber);
          let startIndex;
          let endIndex = currentMonthIndex + 1;
          
          if (currentMonthIndex >= 6) {
            startIndex = currentMonthIndex - 6;
          } else {
            startIndex = Math.max(0, 12 - (7 - currentMonthIndex - 1));
            if (startIndex > currentMonthIndex) {
              const endMonths = yearStats.monthly_trend.slice(startIndex, 12);
              const startMonths = yearStats.monthly_trend.slice(0, endIndex);
              const rollingData = [...endMonths, ...startMonths];
              
              const monthAbbr = {
                'August': 'Aug', 'September': 'Sep', 'October': 'Oct', 'November': 'Nov',
                'December': 'Dec', 'January': 'Jan', 'February': 'Feb', 'March': 'Mar',
                'April': 'Apr', 'May': 'May', 'June': 'Jun', 'July': 'Jul'
              };
              
              const chartData = rollingData.map(d => ({ ...d, monthAbbr: monthAbbr[d.month] }));
              const allCounts = chartData.map(d => d.count);
              const maxCount = Math.max(...allCounts, 1);
              const yMax = Math.ceil(maxCount * 1.2);
              
              return (
                <div className="bg-white rounded-lg shadow-md p-2 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setTrendModalOpen(true)}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold" style={{ color: enconBlack }}>Last 7 Months Trend</h3>
                    <span className="text-xs text-gray-500">Click for details</span>
                  </div>
                  <ResponsiveContainer width="100%" height={90}>
                    <LineChart data={chartData} margin={{ top: 10, right: 15, left: 5, bottom: 5 }}>
                      <XAxis dataKey="monthAbbr" tick={{ fontSize: 10 }} interval={0} height={20} />
                      <YAxis domain={[0, yMax]} tick={{ fontSize: 9 }} width={25} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      {yearStats.error_types_list && yearStats.error_types_list.map((errorType) => (
                        <Line 
                          key={errorType}
                          type="monotone" 
                          dataKey={errorType}
                          stroke={ERROR_TYPE_COLORS[errorType] || '#9333ea'}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      ))}
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        stroke={enconOrange}
                        strokeWidth={2.5}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          const isCurrentMonth = payload.month_number === selectedMonthNumber;
                          return (
                            <circle cx={cx} cy={cy} r={isCurrentMonth ? 6 : 3} fill={enconOrange}
                              stroke={isCurrentMonth ? enconOrange : enconOrange} strokeWidth={isCurrentMonth ? 2 : 0} />
                          );
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            }
          }
          
          const rollingData = yearStats.monthly_trend.slice(startIndex, endIndex);
          const monthAbbr = {
            'August': 'Aug', 'September': 'Sep', 'October': 'Oct', 'November': 'Nov',
            'December': 'Dec', 'January': 'Jan', 'February': 'Feb', 'March': 'Mar',
            'April': 'Apr', 'May': 'May', 'June': 'Jun', 'July': 'Jul'
          };
          
          const chartData = rollingData.map(d => ({ ...d, monthAbbr: monthAbbr[d.month] }));
          const allCounts = chartData.map(d => d.count);
          const maxCount = Math.max(...allCounts, 1);
          const yMax = Math.ceil(maxCount * 1.2);
          
          return (
            <div className="bg-white rounded-lg shadow-md p-2 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setTrendModalOpen(true)}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold" style={{ color: enconBlack }}>Last 7 Months Trend</h3>
                <span className="text-xs text-gray-500">Click for details</span>
              </div>
              <ResponsiveContainer width="100%" height={90}>
                <LineChart data={chartData} margin={{ top: 10, right: 15, left: 5, bottom: 5 }}>
                  <XAxis dataKey="monthAbbr" tick={{ fontSize: 10 }} interval={0} height={20} />
                  <YAxis domain={[0, yMax]} tick={{ fontSize: 9 }} width={25} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  {yearStats.error_types_list && yearStats.error_types_list.map((errorType) => (
                    <Line 
                      key={errorType}
                      type="monotone" 
                      dataKey={errorType}
                      stroke={ERROR_TYPE_COLORS[errorType] || '#9333ea'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke={enconOrange}
                    strokeWidth={2.5}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      const isCurrentMonth = payload.month_number === selectedMonthNumber;
                      return (
                        <circle cx={cx} cy={cy} r={isCurrentMonth ? 6 : 3} fill={enconOrange}
                          stroke={isCurrentMonth ? enconOrange : enconOrange} strokeWidth={isCurrentMonth ? 2 : 0} />
                      );
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* Charts Section - Bar chart 3/4, Pie chart 1/4 */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Errors by Person - Takes 3/4 width */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Errors by Person
                  <span className="text-xs font-normal text-gray-500">— click a bar to filter the list below</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {errorPersonData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={errorPersonData.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar
                        dataKey="errors"
                        fill={enconOrange}
                        cursor="pointer"
                        onClick={(payload) => {
                          // Recharts passes the data row object as the first
                          // arg when you click on a bar shape.
                          const name = payload?.name || payload?.payload?.name;
                          if (!name) return;
                          // Click again to toggle off (clear filter).
                          setEntryFilterPerson(prev => prev === name ? null : name);
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data</p>
                )}
              </CardContent>
            </Card>

            {/* Errors by Type - Takes 1/4 width, list format - SHOW ALL TYPES */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-sm">Errors by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {['Picking', 'Checking', 'Input', 'Delivery', 'Loading', 'Invoice Query'].map((typeName) => {
                    const count = stats?.errors_by_type?.[typeName] || 0;
                    const color = ERROR_TYPE_COLORS[typeName];
                    const active = entryFilterType === typeName;
                    return (
                      <button
                        key={typeName}
                        onClick={() => setEntryFilterType(prev => prev === typeName ? null : typeName)}
                        className={`w-full flex items-center justify-between p-2 rounded transition-colors ${
                          active ? 'ring-2 ring-offset-1' : 'hover:bg-gray-50'
                        }`}
                        style={active ? { backgroundColor: `${color}1A`, '--tw-ring-color': color } : undefined}
                        data-testid={`error-type-row-${typeName}`}
                        title={active ? 'Click to clear filter' : `Click to filter by ${typeName}`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: color }}
                          ></div>
                          <span className="text-sm">{typeName}</span>
                        </div>
                        <span 
                          className="font-bold text-lg"
                          style={{ color: color }}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Entries List */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-lg font-bold" style={{ color: enconBlack }}>
              {monthNames[calMonthToTabIdx(selectedMonthNumber)]} Entries ({(() => {
                let n = entries.length;
                if (entryFilterPerson) n = entries.filter(e => (e.names || []).includes(entryFilterPerson)).length;
                if (entryFilterType) n = entries.filter(e => {
                  const list = (e.names || []);
                  if (entryFilterPerson && !list.includes(entryFilterPerson)) return false;
                  return (e.error_types || []).includes(entryFilterType);
                }).length;
                return n;
              })()})
            </h3>
            {(entryFilterPerson || entryFilterType) && (
              <div className="flex flex-wrap items-center gap-2" data-testid="entry-filter-pills">
                {entryFilterPerson && (
                  <button
                    onClick={() => setEntryFilterPerson(null)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border-2 hover:bg-orange-100 transition-colors"
                    style={{ borderColor: enconOrange, color: enconOrange }}
                    data-testid="entry-filter-person-pill"
                    title="Click to clear"
                  >
                    Person: {entryFilterPerson} <X className="w-3 h-3" />
                  </button>
                )}
                {entryFilterType && (
                  <button
                    onClick={() => setEntryFilterType(null)}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border-2 hover:bg-purple-100 transition-colors"
                    style={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                    data-testid="entry-filter-type-pill"
                    title="Click to clear"
                  >
                    Type: {entryFilterType} <X className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => { setEntryFilterPerson(null); setEntryFilterType(null); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                  data-testid="entry-filter-clear-all"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          
          {entries.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500">No error entries for this month</p>
                <Button onClick={openAddModal} className="mt-4" style={{ backgroundColor: enconOrange }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Entry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entries
                .filter(e => {
                  if (entryFilterPerson && !(e.names || []).includes(entryFilterPerson)) return false;
                  if (entryFilterType && !(e.error_types || []).includes(entryFilterType)) return false;
                  return true;
                })
                .map((entry) => (
                <div
                  id={`entry-${entry.id}`}
                  key={entry.id}
                  onClick={() => openViewModal(entry)}
                  className={`rounded-lg p-4 border-2 cursor-pointer hover:shadow-md transition-all ${
                    highlightedEntryId === entry.id 
                      ? 'bg-yellow-100 border-yellow-400 ring-4 ring-yellow-300 animate-pulse' 
                      : 'bg-purple-50 border-purple-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-purple-900">Order #{entry.order_number}</div>
                      {entry.customer_name && (
                        <div className="text-sm text-purple-700">{entry.customer_name}</div>
                      )}
                    </div>
                    <div className="text-right flex items-start gap-2">
                      <div>
                        <div className="text-xs text-purple-600">{fmtDate(entry.date)}</div>
                        {entry.created_by && (
                          <div className="text-xs text-purple-500 font-medium">
                            Added by: {entry.created_by.split('@')[0]}
                          </div>
                        )}
                      </div>
                      {user?.role === 'super_admin' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this error log entry?')) {
                              handleDeleteEntry(entry.id);
                            }
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
                          title="Delete entry"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {(entry.sales_value > 0 || entry.contribution > 0 || entry.cost > 0) && (
                    <div className="flex gap-3 mb-2 text-xs">
                      {entry.sales_value > 0 && (
                        <div className="text-purple-700">
                          Sales: <span className="font-semibold">£{entry.sales_value.toFixed(2)}</span>
                        </div>
                      )}
                      {entry.contribution > 0 && (
                        <div className="text-purple-700">
                          Contrib: <span className="font-semibold">£{entry.contribution.toFixed(2)}</span>
                        </div>
                      )}
                      {entry.cost > 0 && (
                        <div className="text-red-700">
                          Cost: <span className="font-semibold">£{entry.cost.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mb-2">
                    <div className="text-xs text-purple-600 mb-1">Error Types:</div>
                    <div className="flex flex-wrap gap-1">
                      {entry.error_types?.map((type, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {entry.names && entry.names.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-purple-600 mb-1">People:</div>
                      <div className="text-sm text-purple-800">{entry.names.join(', ')}</div>
                    </div>
                  )}
                  
                  {entry.what_happened && (
                    <div className="text-xs text-red-600 mb-2 whitespace-pre-wrap font-medium">
                      {entry.what_happened}
                    </div>
                  )}
                  
                  {entry.resolution && (
                    <div className="text-xs text-green-700 italic whitespace-pre-wrap">
                      <strong>Resolution:</strong> {entry.resolution}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Service Issues Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="h-px flex-grow bg-orange-400"></div>
            <h2 className="mx-4 text-xl font-bold text-orange-600">Service Issues</h2>
            <div className="h-px flex-grow bg-orange-400"></div>
          </div>
          
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => {
                setEditingServiceIssue(null);
                setServiceIssueModalOpen(true);
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-white flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Service Issue
            </Button>
          </div>
          
          {serviceIssues.length === 0 ? (
            <Card className="bg-yellow-50 border-2 border-yellow-200">
              <CardContent className="p-8 text-center">
                <div className="text-yellow-600 mb-2">No service issues logged for this month</div>
                <Button
                  onClick={() => {
                    setEditingServiceIssue(null);
                    setServiceIssueModalOpen(true);
                  }}
                  className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white"
                >
                  Add First Service Issue
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {serviceIssues.map((issue) => (
                <div
                  key={issue.id}
                  onClick={() => {
                    setViewingServiceIssue(issue);
                    setServiceIssueModalOpen(true);
                  }}
                  className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-300 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-yellow-900">
                        {issue.vehicle_registration}
                      </div>
                      {issue.customer_name && (
                        <div className="text-sm text-yellow-800">{issue.customer_name}</div>
                      )}
                    </div>
                    <div className="text-xs text-yellow-600">{fmtDate(issue.date)}</div>
                  </div>
                  
                  {issue.order_number && (
                    <div className="text-sm text-yellow-800 mb-2">
                      Order: <span className="font-semibold">{issue.order_number}</span>
                    </div>
                  )}
                  
                  {(issue.miles > 0 || issue.contribution > 0) && (
                    <div className="flex gap-3 mb-2 text-xs">
                      {issue.miles > 0 && (
                        <div className="text-yellow-700">
                          Miles: <span className="font-semibold">{issue.miles}</span>
                        </div>
                      )}
                      {issue.contribution > 0 && (
                        <div className="text-yellow-700">
                          Contrib: <span className="font-semibold">£{issue.contribution.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {issue.category && (
                    <div className="mb-2">
                      <span className="px-3 py-1 bg-yellow-200 text-yellow-900 rounded-full text-sm font-semibold">
                        {issue.category}
                      </span>
                    </div>
                  )}
                  
                  {issue.notes && (
                    <div className="text-xs text-gray-700 mt-2 whitespace-pre-wrap">
                      {issue.notes}
                    </div>
                  )}
                  
                  {issue.postcode && (
                    <div className="text-xs text-yellow-600 mt-2">
                      📍 {issue.postcode}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Add/Edit Modal */}
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogContent 
        className="max-w-3xl max-h-[70vh] overflow-y-auto bg-white"
        style={{
          border: '4px solid transparent',
          backgroundImage: `linear-gradient(white, white), linear-gradient(135deg, ${enconOrange}, #ec4899)`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          marginTop: '8vh',
          marginBottom: '8vh',
          borderRadius: '12px'
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: enconBlack }}>
            {editingEntry ? 'Edit Error Entry' : 'Add Error Entry'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Date - Moved to top */}
          <div>
            <Label>Date *</Label>
            <Input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              autoFocus={false}
            />
          </div>

          {/* Order Number and Customer Name - side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Order Number *</Label>
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                onBlur={() => lookupOrderDetails(orderNumber)}
                placeholder="Enter order number"
              />
            </div>
            <div>
              <Label>Customer Name</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Customer name"
              />
            </div>
          </div>

          {/* CSV Lookup Results Box - Orange to Pink Gradient Border */}
          {(customerName || salesValue || contribution) && (
            <div 
              className="p-3 bg-white rounded-lg"
              style={{
                border: '3px solid transparent',
                backgroundImage: `linear-gradient(white, white), linear-gradient(135deg, ${enconOrange}, #ec4899)`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                borderRadius: '8px'
              }}
            >
              <div className="text-xs font-semibold mb-2" style={{ color: enconOrange }}>
                📋 Order Details from CSV
              </div>
              <div className="space-y-1">
                {customerName && (
                  <div className="text-sm">
                    <span className="text-gray-600">Customer:</span>{' '}
                    <span className="font-semibold text-gray-900">{customerName}</span>
                  </div>
                )}
                {salesValue && (
                  <div className="text-sm">
                    <span className="text-gray-600">Sales Value:</span>{' '}
                    <span className="font-bold text-purple-600">£{parseFloat(salesValue).toFixed(2)}</span>
                  </div>
                )}
                {contribution && (
                  <div className="text-sm">
                    <span className="text-gray-600">Contribution:</span>{' '}
                    <span className="font-bold text-green-600">£{parseFloat(contribution).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Names */}
          <div>
            <Label>Name(s) *</Label>
            {names.map((name, index) => (
              <div key={index} className="mb-2">
                <div className="flex gap-2">
                  <Input
                    value={name}
                    onChange={(e) => updateName(index, e.target.value)}
                    placeholder="Enter name"
                    className="flex-1"
                  />
                  {names.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeName(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {/* Similar name suggestion banner */}
                {nameSimilarSuggestions[index] && nameSimilarSuggestions[index].length > 0 && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-amber-800 font-medium mb-2">
                          Similar name(s) found. Did you mean:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {nameSimilarSuggestions[index].map((suggestion, sIdx) => (
                            <button
                              key={sIdx}
                              type="button"
                              onClick={() => selectSimilarName(index, suggestion)}
                              className="px-3 py-1 bg-white border border-amber-300 rounded-full text-sm text-amber-800 hover:bg-amber-100 transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => dismissSimilarSuggestion(index)}
                            className="px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-sm text-gray-600 hover:bg-gray-200 transition-colors"
                          >
                            Keep "{name}"
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addName}
              className="mt-1"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Name
            </Button>
          </div>

          {/* Error Types with + button */}
          <div>
            <Label>Error Type(s) *</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {availableErrorTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleErrorType(type)}
                  className={`px-3 py-1 rounded-lg text-sm transition-all ${
                    errorTypes.includes(type)
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={errorTypes.includes(type) ? { backgroundColor: enconOrange } : {}}
                >
                  {type}
                </button>
              ))}
              
              {/* + Button to add new error type */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAddErrorType(!showAddErrorType);
                }}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all"
                title="Add new error type"
              >
                <Plus className="w-4 h-4" style={{ color: enconOrange }} />
              </button>
            </div>
            
            {/* Add error type input - shown when + is clicked */}
            {showAddErrorType && (
              <div className="flex gap-2 mt-2">
                <Input
                  value={customErrorType}
                  onChange={(e) => setCustomErrorType(e.target.value)}
                  placeholder="Enter new error type"
                  className="flex-1"
                  autoFocus={false}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && customErrorType) {
                      addCustomErrorType();
                      setShowAddErrorType(false);
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={() => {
                    addCustomErrorType();
                    setShowAddErrorType(false);
                  }}
                  disabled={!customErrorType}
                  style={{ backgroundColor: enconOrange }}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddErrorType(false);
                    setCustomErrorType('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* What Happened */}
          <div>
            <Label>What Happened *</Label>
            <textarea
              value={whatHappened}
              onChange={(e) => setWhatHappened(e.target.value)}
              placeholder="Describe what happened..."
              className="w-full min-h-[100px] p-2 border rounded-lg"
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Resolution */}
          <div>
            <Label>Resolution</Label>
            <textarea
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Describe the resolution..."
              className="w-full min-h-[80px] p-2 border rounded-lg"
              style={{ resize: 'vertical' }}
            />
          </div>
          
          {/* Cost */}
          <div>
            <Label>Cost (£)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="Enter cost if applicable"
            />
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          {editingEntry && (
            <Button
              variant="destructive"
              onClick={() => {
                if (editingEntry.id) {
                  handleDeleteEntry(editingEntry.id);
                  setModalOpen(false);
                } else {
                  toast.error('Cannot delete: Entry ID not found');
                }
              }}
            >
              Delete Entry
            </Button>
          )}
          
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} style={{ backgroundColor: enconOrange }}>
              {editingEntry ? 'Update' : 'Create'} Entry
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>


    {/* View Entry Modal */}
    <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
      <DialogContent 
        className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white"
        style={{
          border: '4px solid transparent',
          backgroundImage: `linear-gradient(white, white), linear-gradient(135deg, ${enconOrange}, #ec4899)`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          marginTop: '5vh',
          marginBottom: '5vh',
          borderRadius: '12px'
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: enconBlack }}>
            Error Report - Order #{viewingEntry?.order_number}
          </DialogTitle>
        </DialogHeader>
        
        {viewingEntry && (
          <div className="space-y-4 py-4">
            {/* Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-600 text-xs">Date</Label>
                <div className="font-semibold">{fmtDate(viewingEntry.date)}</div>
              </div>
              <div>
                <Label className="text-gray-600 text-xs">Order Number</Label>
                <div className="font-semibold">{viewingEntry.order_number}</div>
              </div>
            </div>

            {/* Customer Info */}
            {(viewingEntry.customer_name || viewingEntry.sales_value > 0 || viewingEntry.contribution > 0) && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-blue-900 text-sm font-semibold mb-2 block">Order Details</Label>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  {viewingEntry.customer_name && (
                    <div>
                      <span className="text-blue-700">Customer:</span>
                      <div className="font-semibold text-blue-900">{viewingEntry.customer_name}</div>
                    </div>
                  )}
                  {viewingEntry.sales_value > 0 && (
                    <div>
                      <span className="text-blue-700">Sales Value:</span>
                      <div className="font-semibold text-blue-900">£{viewingEntry.sales_value.toFixed(2)}</div>
                    </div>
                  )}
                  {viewingEntry.contribution > 0 && (
                    <div>
                      <span className="text-blue-700">Contribution:</span>
                      <div className="font-semibold text-blue-900">£{viewingEntry.contribution.toFixed(2)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* People Involved */}
            {viewingEntry.names && viewingEntry.names.length > 0 && (
              <div>
                <Label className="text-gray-600 text-xs">People Involved</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {viewingEntry.names.map((name, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-purple-100 text-purple-800 rounded-lg text-sm font-medium"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Error Types */}
            {viewingEntry.error_types && viewingEntry.error_types.length > 0 && (
              <div>
                <Label className="text-gray-600 text-xs">Error Types</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {viewingEntry.error_types.map((type, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-lg text-sm font-medium text-white"
                      style={{ backgroundColor: enconOrange }}
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* What Happened */}
            {viewingEntry.what_happened && (
              <div>
                <Label className="text-gray-600 text-xs">What Happened</Label>
                <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 whitespace-pre-wrap">
                  {viewingEntry.what_happened}
                </div>
              </div>
            )}

            {/* Resolution */}
            {viewingEntry.resolution && (
              <div>
                <Label className="text-gray-600 text-xs">Resolution</Label>
                <div className="mt-1 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 whitespace-pre-wrap">
                  {viewingEntry.resolution}
                </div>
              </div>
            )}

            {/* Cost */}
            {viewingEntry.cost > 0 && (
              <div>
                <Label className="text-gray-600 text-xs">Cost</Label>
                <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-700 font-semibold text-lg">£{viewingEntry.cost.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Created By */}
            {viewingEntry.created_by && (
              <div className="text-xs text-gray-500 pt-2 border-t">
                <span>Added by: </span>
                <span className="font-medium">{viewingEntry.created_by.split('@')[0]}</span>
                {viewingEntry.created_at && (
                  <span className="ml-2">({new Date(viewingEntry.created_at).toLocaleString()})</span>
                )}
              </div>
            )}

            {/* Related Entries */}
            {relatedEntries.length > 0 && (
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Other Errors by Same Person</Label>
                <div className="mt-2 space-y-2">
                  {relatedEntries.map((entry) => (
                    <div
                      key={entry.id}
                      onClick={() => openViewModal(entry)}
                      className="p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div className="font-semibold text-purple-900 text-sm">Order #{entry.order_number}</div>
                        <div className="text-xs text-purple-600">{fmtDate(entry.date)}</div>
                      </div>
                      {entry.error_types && entry.error_types.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {entry.error_types.map((type, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 bg-purple-200 text-purple-800 rounded">
                              {type}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setViewModalOpen(false);
                openEditModal(viewingEntry);
              }}
              style={{ backgroundColor: enconOrange }}
            >
              Edit Entry
            </Button>
            {user?.role === 'super_admin' && viewingEntry && (
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to delete this error log entry? This cannot be undone.')) return;
                  await handleDeleteEntry(viewingEntry.id);
                  setViewModalOpen(false);
                }}
                data-testid="delete-error-entry-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
          
          <Button variant="outline" onClick={() => setViewModalOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Trend Detail Modal */}
    <Dialog open={trendModalOpen} onOpenChange={setTrendModalOpen}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto bg-white rounded-xl">
        <DialogHeader>
          <DialogTitle style={{ color: enconBlack }}>
            Monthly Trend Analysis - {selectedCalendarYear}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Large Line Chart */}
          {stats && stats.monthly_trend && (
            <div>
              <h3 className="text-base font-semibold mb-3" style={{ color: enconBlack }}>
                Error Count by Month
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }} 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                  />
                  <YAxis 
                    label={{ value: 'Error Count', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ fontSize: 14 }}
                    formatter={(value) => [`${value} errors`, 'Count']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke={enconOrange} 
                    strokeWidth={3}
                    dot={{ fill: enconOrange, r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Statistics Table */}
          {stats && stats.monthly_trend && (
            <div>
              <h3 className="text-base font-semibold mb-3" style={{ color: enconBlack }}>
                Monthly Breakdown
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-2 border">Month</th>
                      <th className="text-right p-2 border">Error Count</th>
                      <th className="text-right p-2 border">% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.monthly_trend.map((month, index) => {
                      const totalErrors = stats.monthly_trend.reduce((sum, m) => sum + m.count, 0);
                      const percentage = totalErrors > 0 ? ((month.count / totalErrors) * 100).toFixed(1) : 0;
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="p-2 border">{month.month}</td>
                          <td className="text-right p-2 border font-semibold">{month.count}</td>
                          <td className="text-right p-2 border">{percentage}%</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-orange-50 font-bold">
                      <td className="p-2 border">Total</td>
                      <td className="text-right p-2 border">
                        {stats.monthly_trend.reduce((sum, m) => sum + m.count, 0)}
                      </td>
                      <td className="text-right p-2 border">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Insights */}
          {stats && stats.monthly_trend && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-base font-semibold mb-2 text-blue-900">Key Insights</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>
                  • Highest errors: <strong>
                    {(() => {
                      const max = Math.max(...stats.monthly_trend.map(m => m.count));
                      const maxMonth = stats.monthly_trend.find(m => m.count === max);
                      return `${maxMonth?.month} (${max} errors)`;
                    })()}
                  </strong>
                </li>
                <li>
                  • Lowest errors: <strong>
                    {(() => {
                      const min = Math.min(...stats.monthly_trend.map(m => m.count));
                      const minMonth = stats.monthly_trend.find(m => m.count === min);
                      return `${minMonth?.month} (${min} errors)`;
                    })()}
                  </strong>
                </li>
                <li>
                  • Average per month: <strong>
                    {(stats.monthly_trend.reduce((sum, m) => sum + m.count, 0) / 12).toFixed(1)} errors
                  </strong>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => setTrendModalOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Service Issue Modal */}
    <Dialog open={serviceIssueModalOpen} onOpenChange={(open) => {
      setServiceIssueModalOpen(open);
      if (!open) {
        setViewingServiceIssue(null);
        setEditingServiceIssue(null);
      }
    }}>
      <DialogContent className="max-w-2xl bg-white rounded-2xl border-2 border-yellow-400">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-yellow-600 flex items-center gap-2">
            <AlertCircle className="w-6 h-6" />
            {viewingServiceIssue ? 'Service Issue Details' : 'Add Service Issue'}
          </DialogTitle>
        </DialogHeader>
        
        {viewingServiceIssue ? (
          /* View Mode */
          <div className="space-y-4 pt-4">
            <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-semibold text-gray-600">Date:</div>
                  <div className="text-gray-900">{fmtDate(viewingServiceIssue.date)}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Vehicle:</div>
                  <div className="text-gray-900">{viewingServiceIssue.vehicle_registration}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Customer:</div>
                  <div className="text-gray-900">{viewingServiceIssue.customer_name || '—'}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Order Number:</div>
                  <div className="text-gray-900">{viewingServiceIssue.order_number || '—'}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Postcode:</div>
                  <div className="text-gray-900">{viewingServiceIssue.postcode || '—'}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Miles:</div>
                  <div className="text-gray-900">{viewingServiceIssue.miles || 0}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Contribution:</div>
                  <div className="text-gray-900">£{(viewingServiceIssue.contribution || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-600">Category:</div>
                  <div>
                    <span className="px-3 py-1 bg-yellow-200 text-yellow-900 rounded-full text-sm font-semibold">
                      {viewingServiceIssue.category}
                    </span>
                  </div>
                </div>
              </div>
              
              {viewingServiceIssue.notes && (
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <div className="font-semibold text-gray-600 mb-2">Notes:</div>
                  <div className="text-gray-900 whitespace-pre-wrap">{viewingServiceIssue.notes}</div>
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-yellow-200 text-xs text-gray-500">
                <div>Created by: {viewingServiceIssue.created_by}</div>
                <div>Created at: {new Date(viewingServiceIssue.created_at).toLocaleString()}</div>
              </div>
            </div>
            
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="destructive"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this service issue?')) {
                    try {
                      await api.delete(`${API}/service-issues/${viewingServiceIssue.id}`);
                      toast.success('Service issue deleted');
                      setServiceIssueModalOpen(false);
                      setViewingServiceIssue(null);
                      loadServiceIssues();
                    } catch (error) {
                      console.error('Error deleting service issue:', error);
                      toast.error('Failed to delete service issue');
                    }
                  }
                }}
              >
                Delete
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingServiceIssue(viewingServiceIssue);
                    setViewingServiceIssue(null);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setServiceIssueModalOpen(false);
                    setViewingServiceIssue(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Add/Edit Mode */
          <div className="space-y-4 pt-4">
            <div className="text-sm text-gray-600 mb-4">
              {editingServiceIssue ? 'Update the service issue details below.' : 'Create a new service issue entry manually. This can also be created from the Transport page.'}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  defaultValue={editingServiceIssue?.date || new Date().toISOString().split('T')[0]}
                  id="si-date"
                />
              </div>
              <div>
                <Label>Vehicle Registration *</Label>
                <Input
                  placeholder="e.g., VX24 YXK"
                  defaultValue={editingServiceIssue?.vehicle_registration || ''}
                  id="si-vehicle"
                />
              </div>
              <div>
                <Label>Customer Name</Label>
                <Input
                  placeholder="Customer name"
                  defaultValue={editingServiceIssue?.customer_name || ''}
                  id="si-customer"
                />
              </div>
              <div>
                <Label>Order Number</Label>
                <Input
                  placeholder="Order number"
                  defaultValue={editingServiceIssue?.order_number || ''}
                  id="si-order"
                />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input
                  placeholder="e.g., SW1A 1AA"
                  defaultValue={editingServiceIssue?.postcode || ''}
                  id="si-postcode"
                />
              </div>
              <div>
                <Label>Miles</Label>
                <Input
                  type="number"
                  placeholder="0"
                  defaultValue={editingServiceIssue?.miles || 0}
                  id="si-miles"
                />
              </div>
              <div>
                <Label>Contribution (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  defaultValue={editingServiceIssue?.contribution || 0}
                  id="si-contribution"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  defaultValue={editingServiceIssue?.category || ''}
                  id="si-category"
                >
                  <option value="">Select category...</option>
                  <option value="Over Committed">Over Committed</option>
                  <option value="Operational">Operational</option>
                  <option value="Resource">Resource</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded-md resize-none"
                rows={3}
                placeholder="Describe the service issue..."
                defaultValue={editingServiceIssue?.notes || ''}
                id="si-notes"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setServiceIssueModalOpen(false);
                  setEditingServiceIssue(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                onClick={async () => {
                  const date = document.getElementById('si-date').value;
                  const vehicle = document.getElementById('si-vehicle').value;
                  const category = document.getElementById('si-category').value;
                  
                  if (!date || !vehicle || !category) {
                    toast.error('Please fill in required fields (Date, Vehicle, Category)');
                    return;
                  }
                  
                  try {
                    const issueData = {
                      entry_id: `manual_${Date.now()}`,
                      date,
                      vehicle_registration: vehicle,
                      customer_name: document.getElementById('si-customer').value,
                      order_number: document.getElementById('si-order').value,
                      postcode: document.getElementById('si-postcode').value,
                      miles: parseFloat(document.getElementById('si-miles').value) || 0,
                      contribution: parseFloat(document.getElementById('si-contribution').value) || 0,
                      category,
                      notes: document.getElementById('si-notes').value
                    };
                    
                    if (editingServiceIssue) {
                      await api.put(`${API}/service-issues/${editingServiceIssue.id}`, issueData);
                      toast.success('Service issue updated');
                    } else {
                      await api.post(`${API}/service-issues`, issueData);
                      toast.success('Service issue created');
                    }
                    
                    setServiceIssueModalOpen(false);
                    setEditingServiceIssue(null);
                    loadServiceIssues();
                  } catch (error) {
                    console.error('Error saving service issue:', error);
                    toast.error(`Failed to save: ${error.response?.data?.detail || error.message}`);
                  }
                }}
              >
                {editingServiceIssue ? 'Update' : 'Create'} Service Issue
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* View Reports Modal */}
    <Dialog open={viewReportsModalOpen} onOpenChange={setViewReportsModalOpen}>
      <DialogContent 
        className="max-w-4xl max-h-[80vh] overflow-y-auto"
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '3px solid',
          borderImage: 'linear-gradient(135deg, #ee7203, #ff69b4) 1'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
            Saved Reports
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {reports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: enconGrey }} />
              <p className="text-gray-500">No reports created yet</p>
              <Button
                onClick={() => {
                  setViewReportsModalOpen(false);
                  openCreateReportModal();
                }}
                className="mt-4"
                style={{ backgroundColor: enconOrange, color: 'white' }}
              >
                Create Your First Report
              </Button>
            </div>
          ) : (
            reports
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map((report) => (
                <div
                  key={report.id}
                  className="p-4 rounded-lg border-2 hover:shadow-md transition-shadow"
                  style={{ borderColor: enconLightGrey }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 cursor-pointer" onClick={() => viewReport(report)}>
                      <h3 className="font-semibold text-lg hover:text-orange-600 transition-colors" style={{ color: enconBlack }}>
                        {report.name}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(report.created_at), 'dd MMM yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Filter className="w-4 h-4" />
                          {report.filters?.dateRange || 'Custom'} Range
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {report.filters?.includeErrors && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                            Errors
                          </span>
                        )}
                        {report.filters?.includeServiceIssues && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            Service Issues
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewReport(report)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View Report"
                        style={{ color: enconOrange }}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => editReport(report)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit Report"
                        style={{ color: enconOrange }}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => exportReportFromList(report, 'excel')}
                        className="p-2 hover:bg-green-100 rounded-lg transition-colors text-green-600"
                        title="Export to Excel"
                      >
                        <FileSpreadsheet className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => exportReportFromList(report, 'pdf')}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                        title="Export to PDF"
                      >
                        <FileType className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => emailReport(report)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Email Report"
                        style={{ color: enconOrange }}
                      >
                        <Mail className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteReport(report.id)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                        title="Delete Report"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Create/Edit Report Modal */}
    <Dialog open={createReportModalOpen} onOpenChange={setCreateReportModalOpen}>
      <DialogContent 
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '3px solid',
          borderImage: 'linear-gradient(135deg, #ee7203, #ff69b4) 1'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
            {editingReport ? 'Edit Report' : 'Create New Report'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Date Range Selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block" style={{ color: enconBlack }}>
              Date Range
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {['month', 'quarter', 'year', 'custom'].map((range) => (
                <button
                  key={range}
                  onClick={() => handleDateRangeChange(range)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    reportFilters.dateRange === range
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-300 hover:border-orange-300'
                  }`}
                  style={{ color: reportFilters.dateRange === range ? enconOrange : enconBlack }}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range */}
          {reportFilters.dateRange === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium" style={{ color: enconBlack }}>
                  Start Date
                </Label>
                <Input
                  type="date"
                  value={reportFilters.startDate}
                  onChange={(e) => setReportFilters({ ...reportFilters, startDate: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium" style={{ color: enconBlack }}>
                  End Date
                </Label>
                <Input
                  type="date"
                  value={reportFilters.endDate}
                  onChange={(e) => setReportFilters({ ...reportFilters, endDate: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {/* Include Toggles */}
          <div className="grid grid-cols-2 gap-4">
            <div 
              className="p-4 rounded-lg border-2 cursor-pointer transition-all"
              style={{ 
                borderColor: reportFilters.includeErrors ? enconOrange : enconLightGrey,
                backgroundColor: reportFilters.includeErrors ? '#fff5f0' : 'white'
              }}
              onClick={() => setReportFilters({ ...reportFilters, includeErrors: !reportFilters.includeErrors })}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium" style={{ color: enconBlack }}>
                  Include Errors
                </span>
                <div 
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                    reportFilters.includeErrors ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                  }`}
                >
                  {reportFilters.includeErrors && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
            </div>

            <div 
              className="p-4 rounded-lg border-2 cursor-pointer transition-all"
              style={{ 
                borderColor: reportFilters.includeServiceIssues ? enconOrange : enconLightGrey,
                backgroundColor: reportFilters.includeServiceIssues ? '#fff5f0' : 'white'
              }}
              onClick={() => setReportFilters({ ...reportFilters, includeServiceIssues: !reportFilters.includeServiceIssues })}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium" style={{ color: enconBlack }}>
                  Include Service Issues
                </span>
                <div 
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                    reportFilters.includeServiceIssues ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                  }`}
                >
                  {reportFilters.includeServiceIssues && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
            </div>
          </div>

          {/* Filter by Error Type */}
          {reportFilters.includeErrors && (
            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: enconBlack }}>
                Filter by Error Type (Optional)
              </Label>
              <div className="flex flex-wrap gap-2">
                {availableErrorTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      if (reportFilters.errorTypes.includes(type)) {
                        setReportFilters({
                          ...reportFilters,
                          errorTypes: reportFilters.errorTypes.filter(t => t !== type)
                        });
                      } else {
                        setReportFilters({
                          ...reportFilters,
                          errorTypes: [...reportFilters.errorTypes, type]
                        });
                      }
                    }}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      reportFilters.errorTypes.includes(type)
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                    style={{ color: reportFilters.errorTypes.includes(type) ? enconOrange : enconBlack }}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Leave empty to include all error types
              </p>
            </div>
          )}

          {/* Filter by People Involved */}
          <div>
            <Label className="text-sm font-medium mb-2 block" style={{ color: enconBlack }}>
              Filter by Person Involved (Optional)
            </Label>
            
            {/* Selected People Tags */}
            {reportFilters.peopleInvolved.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {reportFilters.peopleInvolved.map((person, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    style={{ backgroundColor: '#fff5f0', color: enconOrange, border: `1px solid ${enconOrange}` }}
                  >
                    {person}
                    <X
                      className="w-3 h-3 cursor-pointer hover:opacity-70"
                      onClick={() => removePersonName(person)}
                    />
                  </span>
                ))}
              </div>
            )}
            
            {/* Autocomplete Input */}
            <div className="relative">
              <Input
                placeholder="Type name and press Enter"
                value={peopleInput}
                onChange={(e) => handlePeopleInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && peopleInput.trim()) {
                    e.preventDefault();
                    addPersonName(peopleInput);
                  } else if (e.key === 'Escape') {
                    setShowPeopleSuggestions(false);
                  }
                }}
                onFocus={() => {
                  if (peopleInput.length >= 2) {
                    loadPeopleSuggestions(peopleInput);
                    setShowPeopleSuggestions(true);
                  }
                }}
                autoComplete="off"
              />
              
              {/* Suggestions Dropdown */}
              {showPeopleSuggestions && peopleSuggestions.length > 0 && (
                <div 
                  className="absolute z-[100] w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  style={{ borderColor: enconOrange }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {peopleSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-4 py-2 hover:bg-orange-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setPeopleInput(suggestion);
                        setShowPeopleSuggestions(false);
                        addPersonName(suggestion);
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Type to see suggestions from existing entries
            </p>
          </div>

          {/* Filter by Customer Name (for Service Issues) */}
          {reportFilters.includeServiceIssues && (
            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: enconBlack }}>
                Filter by Customer Name (Optional)
              </Label>
              
              {/* Selected Customer Tags */}
              {reportFilters.customerNames.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {reportFilters.customerNames.map((customer, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ backgroundColor: '#fff5f0', color: enconOrange, border: `1px solid ${enconOrange}` }}
                    >
                      {customer}
                      <X
                        className="w-3 h-3 cursor-pointer hover:opacity-70"
                        onClick={() => removeCustomerName(customer)}
                      />
                    </span>
                  ))}
                </div>
              )}
              
              {/* Autocomplete Input */}
              <div className="relative">
                <Input
                  placeholder="Type customer name and press Enter"
                  value={customerInput}
                  onChange={(e) => handleCustomerInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customerInput.trim()) {
                      e.preventDefault();
                      addCustomerName(customerInput);
                    } else if (e.key === 'Escape') {
                      setShowCustomerSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (customerInput.length >= 2) {
                      loadCustomerSuggestions(customerInput);
                      setShowCustomerSuggestions(true);
                    }
                  }}
                  autoComplete="off"
                />
                
                {/* Suggestions Dropdown */}
                {showCustomerSuggestions && customerSuggestions.length > 0 && (
                  <div 
                    className="absolute z-[100] w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    style={{ borderColor: enconOrange }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {customerSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 hover:bg-orange-50 cursor-pointer transition-colors"
                        onClick={() => {
                          setCustomerInput(suggestion);
                          setShowCustomerSuggestions(false);
                          addCustomerName(suggestion);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Type to see suggestions from existing service issues
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setCreateReportModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={editingReport ? updateReport : createReport}
              style={{ backgroundColor: enconOrange, color: 'white' }}
              className="hover:opacity-90"
            >
              <FileText className="w-4 h-4 mr-2" />
              {editingReport ? 'Update Report' : 'Create Report'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Grouping Similar Names Modal */}
    <Dialog open={groupingModalOpen} onOpenChange={setGroupingModalOpen}>
      <DialogContent 
        className="max-w-2xl"
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '3px solid',
          borderImage: 'linear-gradient(135deg, #ee7203, #ff69b4) 1'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
            <AlertCircle className="w-6 h-6" />
            Similar Names Detected
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <p className="text-gray-700">
            We found similar {groupingType === 'customer' ? 'customer' : 'person'} names in the system. 
            Would you like to group them together for this report?
          </p>
          
          <div className="bg-orange-50 p-4 rounded-lg border-2" style={{ borderColor: enconLightGrey }}>
            <Label className="text-sm font-medium mb-3 block" style={{ color: enconBlack }}>
              Similar Names Found:
            </Label>
            <div className="space-y-2">
              {similarNames.map((name, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2 bg-white rounded"
                >
                  <span className="font-mono text-gray-700">{name}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block" style={{ color: enconBlack }}>
              Select Standard Name to Use:
            </Label>
            <div className="space-y-2">
              {similarNames.map((name, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedStandardName(name)}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    selectedStandardName === name
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-300 hover:border-orange-300'
                  }`}
                  style={{ color: selectedStandardName === name ? enconOrange : enconBlack }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{name}</span>
                    {selectedStandardName === name && (
                      <Check className="w-5 h-5" style={{ color: enconOrange }} />
                    )}
                  </div>
                </button>
              ))}
              
              {/* Custom name option */}
              <div className="pt-2">
                <Input
                  placeholder="Or type a custom standard name"
                  value={selectedStandardName && !similarNames.includes(selectedStandardName) ? selectedStandardName : ''}
                  onChange={(e) => setSelectedStandardName(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> This will update all existing entries to use "{selectedStandardName}" 
              instead of the variations shown above. This change is permanent.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setGroupingModalOpen(false);
                // Add without grouping
                if (groupingType === 'customer') {
                  setReportFilters({
                    ...reportFilters,
                    customerNames: [...reportFilters.customerNames, customerInput]
                  });
                  setCustomerInput('');
                } else {
                  setReportFilters({
                    ...reportFilters,
                    peopleInvolved: [...reportFilters.peopleInvolved, peopleInput]
                  });
                  setPeopleInput('');
                }
              }}
            >
              Skip Grouping
            </Button>
            <Button
              onClick={groupSimilarNames}
              disabled={!selectedStandardName}
              style={{ backgroundColor: enconOrange, color: 'white' }}
              className="hover:opacity-90"
            >
              <Check className="w-4 h-4 mr-2" />
              Group Names
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Report Viewer Modal */}
    <Dialog open={reportViewerOpen} onOpenChange={setReportViewerOpen}>
      <DialogContent 
        className={`max-h-[90vh] overflow-y-auto !z-[100] transition-all duration-300 ${
          notesOpen ? 'max-w-3xl' : 'max-w-6xl'
        }`}
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '3px solid #ee7203',
          marginRight: notesOpen ? '420px' : '0'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
            {selectedReport?.name || 'Report Viewer'}
          </DialogTitle>
        </DialogHeader>

        {/* Action Buttons - positioned below title */}
        <div className="flex justify-end gap-2 -mt-2 mb-4">
          <Button
            onClick={() => editReport(selectedReport)}
            variant="outline"
            size="sm"
            style={{ borderColor: enconOrange, color: enconOrange }}
            className="hover:bg-orange-50"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit Report
          </Button>
          
          {/* Export Dropdown */}
          <div className="relative">
            <Button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              variant="outline"
              size="sm"
              style={{ borderColor: enconOrange, color: enconOrange }}
              className="hover:bg-orange-50"
            >
              <Download className="w-4 h-4 mr-1" />
              Export
              <ChevronDown className="w-4 h-4 ml-1" />
            </Button>
            
            {exportDropdownOpen && (
              <div 
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border-2 z-50"
                style={{ borderColor: enconOrange }}
              >
                <button
                  onClick={() => {
                    exportToExcel();
                    setExportDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-green-50 flex items-center gap-2 text-green-600 rounded-t-lg transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export to Excel
                </button>
                <button
                  onClick={() => {
                    exportToPDF();
                    setExportDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-2 text-red-600 rounded-b-lg transition-colors"
                >
                  <FileType className="w-4 h-4" />
                  Export to PDF
                </button>
              </div>
            )}
          </div>

          <Button
            onClick={() => setNotesOpen(true)}
            variant="outline"
            size="sm"
            style={{ borderColor: enconOrange, color: enconOrange }}
            className="hover:bg-orange-50"
          >
            Add Note <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {reportLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          </div>
        ) : currentReportData ? (
          <div className="space-y-6 overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
        {/* Report content already added above */}
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-orange-50 rounded-lg border-2" style={{ borderColor: enconOrange }}>
                <div className="text-3xl font-bold" style={{ color: enconOrange }}>
                  {currentReportData.summary?.total_errors || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Total Errors</div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-400">
                <div className="text-3xl font-bold text-yellow-600">
                  {currentReportData.summary?.total_service_issues || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Service Issues</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-300">
                <div className="text-3xl font-bold text-purple-600">
                  {currentReportData.summary?.error_types_count || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Error Types</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                <div className="text-3xl font-bold text-green-600">
                  {currentReportData.summary?.customers_count || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Customers</div>
              </div>
            </div>

            {/* Date Range */}
            {currentReportData.summary?.date_range && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  Report Period: {format(new Date(currentReportData.summary.date_range.start), 'dd MMM yyyy')} - {format(new Date(currentReportData.summary.date_range.end), 'dd MMM yyyy')}
                </span>
              </div>
            )}

            {/* Errors by Type */}
            {currentReportData?.errors_by_type && Object.keys(currentReportData.errors_by_type).length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4" style={{ color: enconOrange }}>
                  Errors by Type
                </h3>
                {Object.entries(currentReportData.errors_by_type).map(([errorType, errors]) => (
                  <div key={errorType} className="mb-4">
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-t-lg border-2 border-b-0" style={{ borderColor: enconOrange }}>
                      <h4 className="font-semibold text-lg" style={{ color: enconBlack }}>
                        {errorType}
                      </h4>
                      <span className="px-3 py-1 rounded-full text-sm font-bold" style={{ backgroundColor: enconOrange, color: 'white' }}>
                        {errors.length}
                      </span>
                    </div>
                    <div className="border-2 border-t-0 rounded-b-lg p-4" style={{ borderColor: enconOrange }}>
                      <div className="space-y-2">
                        {errors.map((error, idx) => (
                          <div key={idx} className="p-3 bg-white rounded border hover:shadow-sm transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  {error.order_number && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-sm font-bold rounded">
                                      #{error.order_number}
                                    </span>
                                  )}
                                  <span className="font-medium text-gray-900">
                                    {format(new Date(error.date), 'dd MMM yyyy')}
                                  </span>
                                  {error.customer_name && (
                                    <span className="text-sm text-gray-600">
                                      - {error.customer_name}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {error.description || error.what_happened || 'No description'}
                                </div>
                                {error.names && error.names.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {error.names.map((name, nIdx) => (
                                      <span key={nIdx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Service Issues by Customer */}
            {currentReportData?.service_issues_by_customer && Object.keys(currentReportData.service_issues_by_customer).length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4" style={{ color: enconOrange }}>
                  Service Issues by Customer
                </h3>
                {Object.entries(currentReportData.service_issues_by_customer).map(([customer, issues]) => (
                  <div key={customer} className="mb-4">
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-t-lg border-2 border-b-0 border-yellow-400">
                      <h4 className="font-semibold text-lg text-gray-900">
                        {customer}
                      </h4>
                      <span className="px-3 py-1 bg-yellow-500 text-white rounded-full text-sm font-bold">
                        {issues.length}
                      </span>
                    </div>
                    <div className="border-2 border-t-0 border-yellow-400 rounded-b-lg p-4">
                      <div className="space-y-2">
                        {issues.map((issue, idx) => (
                          <div key={idx} className="p-3 bg-white rounded border hover:shadow-sm transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {format(new Date(issue.date), 'dd MMM yyyy')}
                                  </span>
                                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded font-medium">
                                    {issue.category || issue.issue_type || 'Service Issue'}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  Order: {issue.order_number || 'N/A'} | Postcode: {issue.postcode || 'N/A'}
                                </div>
                                {issue.notes && (
                                  <div className="text-sm text-gray-500 mt-1 italic">
                                    Note: {issue.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Data Message */}
            {(!currentReportData.errors || currentReportData.errors.length === 0) && 
             (!currentReportData.service_issues || currentReportData.service_issues.length === 0) && (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: enconGrey }} />
                <p className="text-gray-500">No data found for the selected filters</p>
              </div>
            )}
          </div>
        ) : null}

        {/* Close Button */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-6">
          <Button
            variant="outline"
            onClick={() => setReportViewerOpen(false)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Notes Modal - Side by Side */}
    <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
      <DialogContent 
        className="max-w-md max-h-[90vh] !z-[100]"
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '3px solid #ee7203',
          position: 'fixed',
          right: '2%',
          top: '50%',
          transform: 'translateY(-50%)',
          left: 'auto'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ color: enconOrange }}>
            Report Notes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <textarea
            value={reportNotes}
            onChange={(e) => setReportNotes(e.target.value)}
            placeholder="Add notes about this report..."
            className="w-full h-64 p-3 border-2 rounded-lg focus:outline-none focus:ring-2 resize-none"
            style={{
              borderColor: enconLightGrey
            }}
          />
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              setReportNotes(selectedReport?.notes || '');
              setNotesOpen(false);
            }}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={saveReportNotes}
            style={{ backgroundColor: enconOrange, color: 'white' }}
            className="flex-1 hover:opacity-90"
          >
            Save Notes
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Confirm Report Name Modal */}
    <Dialog open={confirmNameModalOpen} onOpenChange={setConfirmNameModalOpen}>
      <DialogContent 
        className="max-w-lg"
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '3px solid #ee7203'
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ color: enconOrange }}>
            Confirm Report Name
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            We've generated a name based on your filters. You can use this name or edit it:
          </p>
          
          <div>
            <Label className="text-sm font-medium mb-2 block" style={{ color: enconBlack }}>
              Report Name
            </Label>
            <Input
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="w-full"
              style={{ borderColor: enconOrange }}
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              setConfirmNameModalOpen(false);
              setCreateReportModalOpen(true);
            }}
            className="flex-1"
          >
            Back to Edit
          </Button>
          <Button
            onClick={confirmAndSaveReport}
            style={{ backgroundColor: enconOrange, color: 'white' }}
            className="flex-1 hover:opacity-90"
          >
            Create Report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Duplicate Order Warning Dialog */}
    <Dialog open={duplicateWarningOpen} onOpenChange={setDuplicateWarningOpen}>
      <DialogContent 
        className="max-w-md"
        style={{
          background: 'white',
          borderRadius: '16px',
          border: '3px solid #f59e0b'
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-amber-600">
            <AlertCircle className="w-6 h-6" />
            <span className="text-xl font-bold">Duplicate Order Number</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <p className="text-gray-700">
            An error log entry already exists for order <span className="font-bold text-amber-600">#{orderNumber}</span>.
          </p>
          
          {duplicateEntry && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2">Existing Entry:</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Order:</span> <span className="font-medium">{duplicateEntry.order_number}</span></p>
                <p><span className="text-gray-500">Customer:</span> <span className="font-medium">{duplicateEntry.customer_name || 'N/A'}</span></p>
                <p><span className="text-gray-500">Date:</span> <span className="font-medium">{fmtDate(duplicateEntry.date)}</span></p>
                {duplicateEntry.error_types && duplicateEntry.error_types.length > 0 && (
                  <p><span className="text-gray-500">Error Types:</span> <span className="font-medium">{duplicateEntry.error_types.join(', ')}</span></p>
                )}
                {duplicateEntry.what_happened && (
                  <p className="mt-2"><span className="text-gray-500">What Happened:</span> <span className="font-medium text-gray-700">{duplicateEntry.what_happened}</span></p>
                )}
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-600">
            Do you want to continue adding a new entry for this order number, or discard your entry?
          </p>
        </div>
        
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={handleDuplicateDiscard}
            className="flex-1 border-gray-300 hover:bg-gray-100"
          >
            <X className="w-4 h-4 mr-2" />
            Discard Entry
          </Button>
          <Button
            onClick={handleDuplicateContinue}
            className="flex-1 text-white hover:opacity-90"
            style={{ backgroundColor: '#f59e0b' }}
          >
            <Check className="w-4 h-4 mr-2" />
            Continue Anyway
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ErrorLog;
